import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import {
  Building2,
  ClipboardCheck,
  Home,
  RefreshCw,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import { LandlordDashboard } from "@/components/landlord/LandlordDashboard";
import { isLandlord } from "@/lib/roles";
import { toast } from "sonner";

import { PlanUsageCard } from "@/components/PlanUsageCard";
import { useMe } from "@/hooks/api/useAuth";
import {
  useCompleteTask,
  useDashboardSummary,
  useDismissTask,
  useGenerateAlerts,
} from "@/hooks/api/useTasks";

const SEVERITY_TONE: Record<string, "info" | "warning" | "danger"> = {
  info: "info",
  warning: "warning",
  critical: "danger",
};

const RESOURCE_LINK: Record<string, string> = {
  property: "/properties",
  contract: "/contracts",
  application: "/applications",
};

export function DashboardPage() {
  const { data: me } = useMe();
  if (isLandlord(me)) {
    return <LandlordDashboard firstName={me?.first_name ?? ""} />;
  }
  return <AgencyDashboard />;
}

function AgencyDashboard() {
  const { data: me } = useMe();
  const { data: summary } = useDashboardSummary();
  const complete = useCompleteTask();
  const dismiss = useDismissTask();
  const generate = useGenerateAlerts();

  const stats = [
    { label: "Active listings", value: summary?.properties_published, icon: Home },
    { label: "Properties", value: summary?.properties_total, icon: Building2 },
    { label: "Open contracts", value: summary?.contracts_active, icon: Users },
    {
      label: "Inspections pending",
      value: summary?.inspections_pending,
      icon: ClipboardCheck,
    },
  ];

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </p>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            Welcome{me ? `, ${me.first_name}` : ""}.
          </h1>
          <div className="mt-2 flex flex-wrap gap-2">
            {me?.roles?.map((role) => (
              <Badge key={role} tone="brand">
                {role.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={generate.isPending}
          onClick={() =>
            generate.mutate(undefined, {
              onSuccess: (r) => toast.success(`${r.created} new alert(s)`),
              onError: () => toast.error("Could not generate alerts"),
            })
          }
        >
          <RefreshCw className="size-4" />
          Refresh alerts
        </Button>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="flex items-center gap-4 pt-6">
              <div className="flex size-11 items-center justify-center rounded-lg bg-muted">
                <Icon className="size-5 text-primary" />
              </div>
              <div>
                <p className="font-display text-2xl font-medium tabular-nums">
                  {value ?? "—"}
                </p>
                <p className="text-sm text-muted-foreground">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <PlanUsageCard organizationId={me?.organization_id} />

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Tasks &amp; alerts</CardTitle>
          {summary && (
            <Badge tone={summary.open_tasks ? "warning" : "success"}>
              {summary.open_tasks} open
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          {summary && summary.recent_tasks.length > 0 ? (
            <ul className="space-y-2">
              {summary.recent_tasks.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-3 border-b border-border/60 pb-2 last:border-0"
                >
                  <div className="flex items-center gap-2.5">
                    <Badge tone={SEVERITY_TONE[t.severity] ?? "neutral"} dot>
                      {t.severity}
                    </Badge>
                    <div>
                      <p className="text-sm font-medium">{t.title}</p>
                      {t.description && (
                        <p className="text-xs text-muted-foreground">{t.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {t.resource_type && RESOURCE_LINK[t.resource_type] && t.resource_id && (
                      <Link
                        to={`${RESOURCE_LINK[t.resource_type]}/${t.resource_id}`}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Open
                      </Link>
                    )}
                    <button
                      onClick={() => complete.mutate(t.id)}
                      className="text-xs font-medium text-success hover:underline"
                    >
                      Done
                    </button>
                    <button
                      onClick={() => dismiss.mutate(t.id)}
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      Dismiss
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              All clear — no open tasks.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
